import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { LuChartColumn } from 'react-icons/lu';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import AuthContext from '../shared/AuthContext';
import LearningGuide from '../learning/LearningGuide';
import PlanAwareLink from '../billing/PlanAwareLink';
import PlanTierLabel from '../billing/PlanTierLabel';
import {
  analyticsAccessForPlan,
  FEATURE,
} from '../../config/planFeatures';
import { useUserPlan } from '../../hooks/useUserPlan';
import { usePlanGate } from '../../hooks/usePlanGate';
import '../pages/UserAnalytics.css';
import { REALTIME_INVALIDATE_EVENT } from '../../realtime/realtimeBus';
import './StudyHubAnalytics.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend, Filler);

const chartColors = {
  read: '#2563eb',
  review: '#0ea5e9',
  bar: '#0c4a8a',
  grid: '#e2e8f0',
};

function InsightChip({ label, value, hint }) {
  return (
    <div className="study-hub-analytics-chip">
      <span className="study-hub-analytics-chip-value">{value}</span>
      <span className="study-hub-analytics-chip-label">{label}</span>
      {hint && <span className="study-hub-analytics-chip-hint">{hint}</span>}
    </div>
  );
}

export default function StudyHubAnalytics() {
  const { apiClient } = useContext(AuthContext);
  const { plan, planReady } = useUserPlan();
  const { blockUnlessFeature } = usePlanGate();
  const access = analyticsAccessForPlan(planReady ? plan : 'FREE');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchOverview = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const res = await apiClient.get('/api/analytics/overview');
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Charts unavailable — try again in a moment.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    fetchOverview(false);
  }, [fetchOverview]);

  useEffect(() => {
    const onInvalidate = () => fetchOverview(true);
    window.addEventListener(REALTIME_INVALIDATE_EVENT, onInvalidate);
    return () => window.removeEventListener(REALTIME_INVALIDATE_EVENT, onInvalidate);
  }, [fetchOverview]);

  const summary = data?.summary || {};
  const activity = useMemo(() => data?.activityLast14Days ?? [], [data?.activityLast14Days]);
  const topTopics = useMemo(() => data?.topTopicsRead ?? [], [data?.topTopicsRead]);
  const schedule = data?.reviewSchedule || {};

  const activityChart = useMemo(() => {
    const labels = activity.map((d) => {
      const parts = String(d.date || '').split('-');
      return parts.length === 3 ? `${parts[1]}/${parts[2]}` : d.date;
    });
    return {
      labels,
      datasets: [
        {
          label: 'Reads',
          data: activity.map((d) => d.reads),
          borderColor: chartColors.read,
          backgroundColor: 'rgba(37, 99, 235, 0.12)',
          fill: true,
          tension: 0.35,
        },
        {
          label: 'Reviews graded',
          data: activity.map((d) => d.reviews),
          borderColor: chartColors.review,
          backgroundColor: 'rgba(14, 165, 233, 0.08)',
          fill: true,
          tension: 0.35,
        },
      ],
    };
  }, [activity]);

  const topicsChart = useMemo(() => ({
    labels: topTopics.map((t) => t.topic),
    datasets: [{
      label: 'Questions read',
      data: topTopics.map((t) => t.count),
      backgroundColor: chartColors.bar,
      borderRadius: 6,
    }],
  }), [topTopics]);

  const scheduleRows = [
    { key: 'scheduled', label: 'In schedule' },
    { key: 'dueNow', label: 'Due now' },
    { key: 'neverReviewed', label: 'Not yet graded' },
    { key: 'activeLearning', label: 'Active learning' },
    { key: 'mature', label: 'Mature (7d+ interval)' },
  ];

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 12, padding: 16 } },
    },
    scales: {
      x: { grid: { color: chartColors.grid } },
      y: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: chartColors.grid } },
    },
  };

  return (
    <section id="study-analytics" className="study-hub-analytics sh-block" aria-labelledby="study-analytics-title">
      <header className="study-hub-analytics-head sh-block-head">
        <div className="sh-block-head-main">
          <span className="sh-block-icon" aria-hidden>
            <LuChartColumn />
          </span>
          <div className="study-hub-analytics-head-text sh-block-head-copy">
            <h2 id="study-analytics-title" className="sh-block-title">
              Progress &amp; analytics
              <span className="study-hub-analytics-tier">{access.tierLabel}</span>
            </h2>
            <p className="sh-block-sub">
              {access.advanced
                ? 'Two-week activity, topic breakdown, and review schedule'
                : 'Basic summary stats on Free — upgrade for full charts and JSON export'}
              {' '}— pair with <Link to="/practice">Practice</Link> and{' '}
              <Link to="/review">Review</Link>.
            </p>
          </div>
        </div>
        <Link to="/review" className="sh-block-link study-hub-analytics-cta">
          Open review
        </Link>
      </header>

      <LearningGuide focus="analytics" />

      {loading && !data && (
        <p className="study-hub-analytics-loading" aria-live="polite">Loading charts…</p>
      )}

      {error && <p className="study-hub-analytics-error" role="alert">{error}</p>}

      {!error && data && (
        <>
          <div className="study-hub-analytics-insights" aria-label="Analytics highlights">
            <InsightChip
              label="Library coverage"
              value={`${summary.coveragePercent ?? 0}%`}
              hint={`${summary.readCount ?? 0} read`}
            />
            <InsightChip label="Liked" value={summary.likedCount ?? 0} />
            <InsightChip
              label="Due now"
              value={summary.dueCount ?? 0}
              hint={summary.dueCount > 0 ? 'Clear the queue on Review' : 'Queue clear'}
            />
          </div>

          {access.advanced ? (
            <div className="analytics-grid study-hub-analytics-grid">
              <section className="analytics-panel">
                <h2>Activity (14 days)</h2>
                <p className="analytics-panel-sub">Daily reads and review grades</p>
                <div className="analytics-chart">
                  {activity.length ? (
                    <Line data={activityChart} options={chartOptions} />
                  ) : (
                    <p className="analytics-empty">No activity in the last two weeks yet.</p>
                  )}
                </div>
              </section>

              <section className="analytics-panel">
                <h2>Top topics read</h2>
                <p className="analytics-panel-sub">Where you spend reading time</p>
                <div className="analytics-chart analytics-chart--bar">
                  {topTopics.length ? (
                    <Bar data={topicsChart} options={{ ...chartOptions, indexAxis: topTopics.length > 4 ? 'y' : 'x' }} />
                  ) : (
                    <p className="analytics-empty">Read questions to see topic breakdown.</p>
                  )}
                </div>
              </section>

              <section className="analytics-panel analytics-panel--schedule">
                <h2>Review schedule</h2>
                <p className="analytics-panel-sub">Spaced repetition snapshot</p>
                <ul className="analytics-schedule-list">
                  {scheduleRows.map(({ key, label }) => (
                    <li key={key}>
                      <span>{label}</span>
                      <strong>{schedule[key] ?? 0}</strong>
                    </li>
                  ))}
                </ul>
                <p className="analytics-schedule-foot">
                  Library size: <strong>{summary.libraryTotal ?? 0}</strong> questions
                </p>
              </section>
            </div>
          ) : (
            <div className="study-hub-analytics-upgrade">
              <p className="study-hub-analytics-upgrade-lead">
                Advanced analytics (14-day charts, topic breakdown, full review schedule) is on{' '}
                <strong>Pro</strong> and <strong>Team</strong>. JSON export is in Settings → Data &amp; privacy.
              </p>
              <button
                type="button"
                className="study-hub-analytics-upgrade-btn"
                onClick={() => blockUnlessFeature(FEATURE.ANALYTICS_ADVANCED)}
              >
                Unlock advanced analytics
                <PlanTierLabel tier="Pro" />
              </button>
            </div>
          )}

          {access.advanced && access.exportJson && (
            <p className="study-hub-analytics-export-note">
              JSON export available in{' '}
              <Link to="/settings?tab=data">Settings → Data &amp; privacy</Link>.
            </p>
          )}

          {access.team ? (
            <section className="study-hub-analytics-team" aria-labelledby="study-analytics-team-title">
              <h3 id="study-analytics-team-title" className="study-hub-analytics-team-title">
                Team analytics
                <PlanTierLabel tier="Team" />
              </h3>
              <p className="study-hub-analytics-team-lead">
                Organization learning score, manager KPIs, and org leaderboard — included with your Team plan.
              </p>
              <PlanAwareLink to="/teams" className="study-hub-analytics-team-link">
                Open Teams workspace
              </PlanAwareLink>
            </section>
          ) : access.advanced ? (
            <section className="study-hub-analytics-team study-hub-analytics-team--locked">
              <h3 className="study-hub-analytics-team-title">
                Team analytics
                <PlanTierLabel tier="Team" />
              </h3>
              <p className="study-hub-analytics-team-lead">
                Advanced + team adds org-wide dashboards and leaderboard on the Team plan.
              </p>
              <PlanAwareLink to="/teams" className="study-hub-analytics-team-link">
                Compare Team plan
              </PlanAwareLink>
            </section>
          ) : null}
        </>
      )}
    </section>
  );
}
